/**
 * The backend stores money as integer minor units (kobo); the UI works in whole
 * Naira. These are the only two places the conversion should ever happen — at
 * the API boundary in the service layer.
 */

/** Naira (major) → kobo (minor), for request payloads. */
export function toMinorUnits(naira: number): number {
  return Math.round(naira * 100);
}

/** Kobo (minor) → Naira (major), for reading responses. */
export function fromMinorUnits(minor: number | string | null | undefined): number {
  if (minor === null || minor === undefined) return 0;
  return Math.round(Number(minor)) / 100;
}

/** Format a major-unit amount using the currency supplied by the backend. */
export function formatMoney(amount: number, currency = 'NGN'): string {
  const normalizedCurrency = /^[A-Z]{3}$/.test(currency) ? currency : 'NGN';
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: normalizedCurrency,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Format totals without adding unlike currencies. Finance screens must never
 * present USD + NGN (or any other currencies) as one meaningless number.
 */
export function formatMoneyTotals(
  entries: Array<{ amount: number; currency?: string }>,
  fallbackCurrency = 'NGN',
): string {
  const totals = new Map<string, number>();
  for (const entry of entries) {
    const currency = /^[A-Z]{3}$/.test(entry.currency ?? '')
      ? entry.currency as string
      : fallbackCurrency;
    totals.set(currency, (totals.get(currency) ?? 0) + entry.amount);
  }
  if (totals.size === 0) return formatMoney(0, fallbackCurrency);
  return [...totals.entries()]
    .map(([currency, amount]) => formatMoney(amount, currency))
    .join(' + ');
}

export const VAT_RATE = 0.075;
